import React, { useEffect, useState } from "react";
import "./style.scss";
import Sidebar from "../../../components/sidebar";
import { useOutletContext } from "react-router-dom";
import * as Yup from 'yup';
import { nameRegex, phoneRegExp } from "../../../constants/utlis";
import { LANG } from "../../../constants/language";
import { useFormik } from "formik";
import { toast } from "react-toastify";
import { AxiosError } from "axios";
import clsx from "clsx";
import ErrorText from "../../../core/components/error-text";
import { updateUserProfile } from "../../../services/user.service";
import { setStorageItem } from "../../../services/storage.service";
import { getUserProfile } from "../../../core/data/redux/user/_userAction";
import { useDispatch } from "react-redux";
import { setUserDetail } from "../../../core/data/redux/user/userSlice";


// Define validation schema using Yup
const validationSchema = Yup.object({
  // name: Yup.string().matches(nameRegex, 'The name can only include letters, spaces, hyphens, or apostrophes.').required("Name is required"),
  name: Yup.string()
    .required("Name is required")
    .trim()
    .test(
      "no-whitespace-only",
      "Name cannot be empty or just whitespace",
      (value) => value !== undefined && value.trim().length > 0
    )
    .min(2, "Name must be at least 2 characters")
    .matches(nameRegex, "Name can only contain letters, spaces, hyphens, apostrophes, and periods"),
  // phone: Yup.string().min(10, LANG.MINIMUM_LIMIT_PHONE_CHAR).max(12, LANG.MAXIMUM_LIMIT_PHONE_CHAR).matches(phoneRegExp, 'Phone number is not valid').required("Phone number is required"),
  phone: Yup.string().matches(phoneRegExp, "Phone number is not valid").required("Phone number is required"),
});

// Interface for form values
interface FormValues {
  name: string;
  email: string;
  phone: string;
}


const Profile: React.FC = () => {
  const { isSidebarOpen, onToggleSidebar } = useOutletContext<{ isSidebarOpen: boolean; onToggleSidebar: () => void }>();
  const dispatch: any = useDispatch();

  const [loading, setLoading] = useState(false);
  const [profileImage, setProfileImage] = useState("/assets/img/userCircle.png");
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);

  // Formik setup
  const formik = useFormik<FormValues>({
    initialValues: {
      name: "",
      email: "",
      phone: "",
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setLoading(true);
      try {
        const formData = new FormData();
        formData.append("name", values.name);
        formData.append("phone", values.phone);
        if (selectedFile) {
          formData.append("image", selectedFile);
        }

        const result = await updateUserProfile(formData);
        setStorageItem("userData", JSON.stringify(result.data.data));
        dispatch(setUserDetail(result.data.data));

        toast.success(result.data.message || "Profile updated successfully");
      } catch (error) {
        if (error instanceof AxiosError) {
          toast.error(
            error.response?.data?.error || "Failed to update profile"
          );
        }
      } finally {
        setSubmitting(false);
        setLoading(false);
      }
    },
  });

  useEffect(() => {
    const fetchUserDetails = async () => {
      try {
        const response: any = await dispatch(getUserProfile({}));
        const userData = response?.payload || response;
        console.log('user profile page');

        if (userData) {
          formik.setValues({
            name: `${userData?.first_name || ""} ${userData?.last_name || ""}`.trim(),
            email: userData?.email || "",
            phone: userData?.phone || "",
          });

          if (userData?.image) {
            setProfileImage(userData?.image);
          }
        }
      } catch (err) {
        console.error("Failed to fetch user details:", err);
      }
    };

    fetchUserDetails();
  }, []);

  // Validate image file
  const validateImage = (file: File): boolean => {
    const validTypes = ["image/jpeg", "image/png"];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (!validTypes.includes(file.type)) {
      setImageError("Only JPEG or PNG images are allowed.");
      return false;
    }
    if (file.size > maxSize) {
      setImageError("Image size must be less than 5MB.");
      return false;
    }
    setImageError(null);
    return true;
  };

  // Handle file upload for profile image
  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (validateImage(file)) {
        const imageUrl = URL.createObjectURL(file);
        setProfileImage(imageUrl);
        setSelectedFile(file);
      } else {
        event.target.value = ""; // Clear the input
        setProfileImage("/assets/img/userCircle.png");
        setSelectedFile(null);
      }
    }
  };

  // Check if the profile image is the default image
  const isDefaultImage = profileImage === "/assets/img/userCircle.png";


  return (
    <div className="layout">
      {/* Sidebar */}
      <Sidebar isOpen={isSidebarOpen} />

      {/* Main Content */}
      <main className="content profilePage">
        <div className="profileSettings">
          <h2>Edit Account Details</h2>

          <div className="profile-upload">
            <div className="upload-circle">
              {isDefaultImage && (
                <>
                  <img src={profileImage} alt="userCircle.png" />
                  <span>Upload photo</span>
                </>
              )}
              {!isDefaultImage && (
                <img className="fullImg" src={profileImage} alt="profileImage" />
              )}
              <button className="uploadImg">
                <input
                  type="file"
                  accept="image/jpeg,image/png"
                  onChange={handleImageUpload}
                />
                <img src="/assets/img/circleEdit.svg" alt="circleEdit" />
              </button>
            </div>
          </div>
          {imageError && <ErrorText show={!!imageError} message={imageError} />}


          <div className="title_form">
            <h3>Personal information</h3>

            <form className="account-form" onSubmit={formik.handleSubmit}>
              <div className="form-group mb-0">
                <label>Name</label>
                <input
                  type="text"
                  placeholder="Enter your name"
                  {...formik.getFieldProps("name")}
                  onBlur={(e) => {
                    const trimmedValue = e.target.value.trim();
                    formik.setFieldValue("name", trimmedValue);
                    formik.handleBlur(e); // keep touched state
                  }}
                  className={clsx(
                    "form-control commonInput bg-transparent",
                    { "border border-danger": formik.touched.name && formik.errors.name }
                  )}
                />
                <ErrorText show={formik.touched.name && !!formik.errors.name} message={formik.errors.name} />
              </div>

              <div className="form-group  mb-0">
                <label>Email</label>
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={formik.values.email}
                  disabled
                  className="form-control commonInput bg-transparent"
                />
              </div>

              <div className="form-group mb-0">
                <label>Phone Number</label>
                <input
                  type="tel"
                  placeholder="Enter your phone number"
                  {...formik.getFieldProps("phone")}
                  className={clsx(
                    "form-control commonInput bg-transparent",
                    { "border border-danger": formik.touched.phone && formik.errors.phone }
                  )}
                />
                <ErrorText show={formik.touched.phone && !!formik.errors.phone} message={formik.errors.phone} />
              </div>

              <button
                type="submit"
                className="btn btn-primary primaryButton btn-update d-inline-flex justify-content-center align-items-center w-100"
                disabled={loading || formik.isSubmitting}
              >
                {loading ? "Updating..." : "Update"}
              </button>
            </form>
          </div>
        </div>
      </main>
    </div>
  );
};

export default Profile;
